/**
 * useBlastHandGenerator
 *
 * Blast-specific equivalent of useHandGenerator.
 * Loads hands from the Blast preflop range CSVs
 * (database/blast/preflop/…) rather than the cash-game ranges.
 *
 * Usage:
 *   const { generateBlastHand } = useBlastHandGenerator();
 *   const { hand } = await generateBlastHand(gameConfig);
 *
 * gameConfig shape (from BlastConfigScreen → BlastRangeScreen):
 *   {
 *     format,        // '3way' | 'hu'
 *     position,      // 'BTN' | 'SB' | 'BB'
 *     stack,         // number (bb)
 *     action,        // 'open' | 'call' | 'jam' | 'complete'
 *     subtype,       // 'raise' | 'jam' | 'all' | 'limp' | undefined
 *     villainPos,    // for call: 'BTN' | 'SB' | 'BB'
 *     villainAction, // for call: 'open' | 'jam' | 'limp' | '3bet small' | '3bet large'
 *     filterMode,    // alias for subtype — set by BlastRangeScreen
 *   }
 *
 * filterMode controls which hands are eligible when the range contains
 * mixed actions (open = raise + jam + fold mixed):
 *   'raise'  → only hands with action === 'raise'
 *   'jam'    → only hands with action === 'allin'
 *   'limp'   → only hands with action === 'call' (SB limp/complete)
 *   'all'    → all hands with any non-fold action (frequency > 0)
 *   null     → same as 'all' (default)
 */

import { useCallback, useState } from "react";
import _ from "lodash";
import { getRangeFilePath } from "../../screens/BlastConfigScreen/blastRangeRules";

// ── Card constants ────────────────────────────────────────────────────────────

const SUITS  = ["h", "d", "c", "s"];
const VALUES = ["2","3","4","5","6","7","8","9","T","J","Q","K","A"];

const CARD_RANKS = {
  A:14, K:13, Q:12, J:11, T:10,
  "9":9,"8":8,"7":7,"6":6,"5":5,"4":4,"3":3,"2":2,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const generateDeck = () =>
  SUITS.flatMap(suit => VALUES.map(value => ({ value, suit })));

const orderByRank = (c1, c2) => {
  if (CARD_RANKS[c1.value] >= CARD_RANKS[c2.value]) {
    return { card1Value: c1.value, card1Suit: c1.suit, card2Value: c2.value, card2Suit: c2.suit };
  }
  return { card1Value: c2.value, card1Suit: c2.suit, card2Value: c1.value, card2Suit: c1.suit };
};

/** Build playable card objects for a range-spec hand. */
const specToCards = ({ hand, type }) => {
  if (!hand || hand.length < 2) return null;
  const v1 = hand[0];
  const v2 = hand[1] ?? hand[0];
  if (!VALUES.includes(v1) || !VALUES.includes(v2)) return null;

  if (v1 === v2) {
    // Pocket pair — need two different suits
    const [s1, s2] = _.shuffle(SUITS);
    return { card1Value: v1, card1Suit: s1, card2Value: v2, card2Suit: s2 };
  }

  const suited = (type === "suited") ||
    (hand.length > 2 && hand[2].toLowerCase() === "s");

  if (suited) {
    const suit = _.sample(SUITS);
    return { card1Value: v1, card1Suit: suit, card2Value: v2, card2Suit: suit };
  }

  // Off-suit — two different suits
  const [s1, s2] = _.shuffle(SUITS);
  return { card1Value: v1, card1Suit: s1, card2Value: v2, card2Suit: s2 };
};

const randomHand = () => {
  const deck = _.shuffle(generateDeck());
  return orderByRank(deck[0], deck[1]);
};

// True random deal from a full 52-card deck, retrying on rank-pair collisions
// with usedHandKeys — mirrors the cash-game generator's approach so genuine
// fold-worthy hands can appear at their correct real-world frequency.
const randomHandAvoiding = (usedHandKeys) => {
  const MAX_ATTEMPTS = 200;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const deck = _.shuffle(generateDeck());
    const dealt = orderByRank(deck[0], deck[1]);
    const key = dealt.card1Value + dealt.card2Value;
    if (!usedHandKeys || usedHandKeys.size === 0 || !usedHandKeys.has(key)) return dealt;
  }
  const deck = _.shuffle(generateDeck());
  return orderByRank(deck[0], deck[1]);
};

// Real 52-card-deck combo counts per hand class: pairs=6, suited=4, offsuit=12.
const comboWeight = (fullClass) => {
  if (fullClass.length === 2) return 6;
  return fullClass.endsWith('s') ? 4 : 12;
};

/** Weighted-random pick of one full class string ('AKs','77','94o') by real combo count. */
const pickWeightedClass = (classes) => {
  if (!classes || classes.length === 0) return null;
  const total = classes.reduce((sum, c) => sum + comboWeight(c), 0);
  let r = Math.random() * total;
  for (const c of classes) {
    r -= comboWeight(c);
    if (r <= 0) return c;
  }
  return classes[classes.length - 1];
};

/** Normalise a { hand, type } row (hand = rank pair only, e.g. 'AK') to a full class string ('AKs'). */
const fullClassFromRow = ({ hand, type }) =>
  type === 'pair' ? hand : type === 'suited' ? hand + 's' : hand + 'o';

// ── CSV parsing ───────────────────────────────────────────────────────────────

/**
 * Parse a Blast range CSV.
 * Returns an array of { hand, type, frequency, action } objects.
 * Hands with frequency === 0 are omitted.
 *
 * CSV format (from process_ranges.py output):
 *   hand,type,frequency,action
 *   AA,pair,100,raise
 *   AKs,suited,100,raise
 *   …
 */
const parseBlastRangeCSV = (text) => {
  const lines  = text.trim().split("\n");
  const headers = lines[0].toLowerCase().split(",").map(h => h.trim());

  const handIdx  = headers.indexOf("hand");
  const typeIdx  = headers.indexOf("type");
  const freqIdx  = headers.indexOf("frequency");
  const actIdx   = headers.indexOf("action");

  if (handIdx === -1 || freqIdx === -1) {
    throw new Error(`Invalid blast range CSV — missing required headers. Found: ${headers.join(", ")}`);
  }

  const hands = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(",").map(v => v.trim());
    if (parts.length <= Math.max(handIdx, freqIdx)) continue;

    const hand   = parts[handIdx];
    const type   = typeIdx  !== -1 ? parts[typeIdx].toLowerCase()  : inferType(hand);
    const action = actIdx   !== -1 ? parts[actIdx].toLowerCase()   : "";

    const freqStr = parts[freqIdx];
    let frequency = 0;
    if (freqStr.includes("%")) {
      frequency = parseInt(freqStr.replace("%", ""), 10);
    } else {
      const n = parseFloat(freqStr);
      frequency = !isNaN(n) ? (n <= 1 ? Math.round(n * 100) : Math.round(n)) : 0;
    }

    if (!hand || frequency <= 0) continue;

    hands.push({ hand: hand.replace(/[so]$/, ""), type, frequency, action });
  }

  return hands;
};

const inferType = (hand) => {
  if (hand.length >= 3) {
    const last = hand[hand.length - 1].toLowerCase();
    if (last === "s") return "suited";
    if (last === "o") return "offsuit";
  }
  if (hand.length >= 2 && hand[0] === hand[1]) return "pair";
  return "offsuit";
};

// ── Filter logic ──────────────────────────────────────────────────────────────

/**
 * Extract and sort all distinct raise action strings from a hand list.
 * Returns them ordered from smallest to largest numeric size.
 * e.g. ['raise_2.5', 'raise_3.5'] or ['raise', 'raise_2.5', 'raise_4']
 */
const getRaiseSizes = (hands) => {
  const raiseActions = [...new Set(hands.map(h => h.action).filter(a => a.startsWith('raise')))];
  return raiseActions.sort((a, b) => {
    const numA = parseFloat(a.replace('raise_', '')) || 0;
    const numB = parseFloat(b.replace('raise_', '')) || 0;
    return numA - numB;
  });
};

/**
 * Apply filterMode to a parsed hands array.
 *
 * filterMode:
 *   'raise'       → any raise action (generic, used when only one size exists)
 *   'raise-small' → hands with the numerically smallest raise size
 *   'raise-large' → hands with the numerically largest raise size
 *   'raise-all'   → all raise actions combined (used by 'Both' selection)
 *   'jam'         → action === 'allin'
 *   'limp'        → action === 'call'  (SB completing the blind)
 *   'all'         → any non-fold action (default when filterMode is null/'all')
 */
const applyFilter = (hands, filterMode) => {
  if (!filterMode || filterMode === "all") {
    return hands; // all hands already have frequency > 0
  }

  const mode = filterMode.toLowerCase();

  // Specific raise size (e.g. 'raise_3', 'raise_5.5') — exact match
  if (mode.startsWith("raise_")) {
    return hands.filter(h => h.action === mode);
  }
  // Jam-only sub-range ('allin' filterMode from BB Option → Jam selection)
  if (mode === "allin") {
    return hands.filter(h => h.action === "allin");
  }
  if (mode === "raise") {
    // Generic raise — any raise action (single-size ranges)
    return hands.filter(h => h.action.startsWith("raise"));
  }
  if (mode === "raise-small") {
    const sizes = getRaiseSizes(hands);
    const smallAction = sizes[0];
    return smallAction ? hands.filter(h => h.action === smallAction) : hands;
  }
  if (mode === "raise-large") {
    const sizes = getRaiseSizes(hands);
    const largeAction = sizes[sizes.length - 1];
    return largeAction ? hands.filter(h => h.action === largeAction) : hands;
  }
  if (mode === "raise-all") {
    // Both / all sizes combined
    return hands.filter(h => h.action.startsWith("raise"));
  }
  if (mode === "jam") {
    return hands.filter(h => h.action === "allin");
  }
  // Check-only sub-range (BB Option → Check selection)
  if (mode === "check") {
    return hands.filter(h => h.action === "check");
  }
  // 'limp' or 'call' subtype both filter for call/limp hands (SB completing the blind).
  // SB open CSVs at short stacks (3–9bb) use 'check' for what is functionally a limp
  // (SB cannot check preflop, so 'check' === limp in that context).
  if (mode === "limp" || mode === "call") {
    return hands.filter(h => h.action === "call" || h.action === "limp" || h.action === "check");
  }

  return hands;
};

// ── Gate range filtering ──────────────────────────────────────────────────────

/**
 * For reaction scenarios (hero opened, then faces villain's response), returns the
 * path to the hero's *opening* range CSV and a predicate that selects the hands
 * that qualify for the initial action (raise or limp).
 *
 * Returns null when no gate pre-filtering is needed (e.g., pure open ranges).
 *
 * Why this matters: the reaction CSV (e.g. sb_vs_sb_open_bb_ai) was solved
 * assuming hero could have any hand — but we should only quiz on hands the hero
 * would actually have reached this node with (i.e., hands in the raise/limp range).
 *
 * Applies to both "Call" and "Jam" response quizzes. Example: if AA always
 * raises at open (never limps), then a hand like AA has no business showing
 * up in the "SB limped → BB raised → SB jams" quiz — SB can't be holding AA
 * at that node in the first place. Previously this only gated the Call
 * response, leaving the Jam response unfiltered.
 */
const getGateRangeInfo = (gameConfig) => {
  const { format, position, stack, primaryAction, action } = gameConfig;

  // Only gate when hero is reacting AND we know their initial opening action.
  if (action !== 'call' && action !== 'jam') return null;
  if (primaryAction !== 'open_raise' && primaryAction !== 'open_limp') return null;
  if (!stack) return null;

  const base       = 'database/blast/preflop';
  const formatPath = format === '3way' ? '3_way' : 'hu';
  const pos        = (position || '').toLowerCase();
  const openDir    = `${pos}_open`;
  const path       = `${base}/${formatPath}/${openDir}/${openDir}_${stack}bb.csv`;

  // Which actions in the opening CSV qualify for THIS scenario?
  const filter = primaryAction === 'open_raise'
    ? (a) => a.startsWith('raise')          // only raise-opened hands
    : (a) => a === 'call' || a === 'check'; // only limped hands

  return { path, filter };
};

/**
 * Given a list of parsed hands (from an opening range), builds a Set of
 * normalised hand keys ('AA', 'AKs', 'AKo') that pass the predicate.
 */
const buildGateKeySet = (hands, predicate) => {
  const keys = new Set();
  hands.forEach(({ hand, type, action }) => {
    if (!predicate(action)) return;
    const suffix = type === 'pair' ? '' : type === 'suited' ? 's' : 'o';
    keys.add(hand + suffix);
  });
  return keys;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useBlastHandGenerator = () => {
  // Cache: rangePath → parsed hands array
  const [cache, setCache] = useState({});

  const generateBlastHand = useCallback(async (gameConfig) => {
    if (!gameConfig) {
      console.warn("useBlastHandGenerator: no gameConfig provided, returning random hand");
      return { hand: randomHand(), rangeMap: new Map(), wasLive: false };
    }

    // getRangeFilePath uses 'position' but GameContext stores it as 'heroPosition' too.
    // Normalise so both keys are present.
    const normalised = {
      ...gameConfig,
      position: gameConfig.position ?? gameConfig.heroPosition,
    };

    const rangePath = getRangeFilePath(normalised);
    if (!rangePath) {
      console.error("useBlastHandGenerator: could not resolve range path for config", normalised);
      return { hand: randomHand(), rangeMap: new Map(), wasLive: false };
    }

    const filterMode = normalised.filterMode ?? normalised.subtype ?? null;

    try {
      // ── Load & cache ──────────────────────────────────────────────────────
      let parsedHands = cache[rangePath];

      if (!parsedHands) {
        const url = rangePath.startsWith("/") ? rangePath : `/${rangePath}`;
        const controller = new AbortController();
        const timeout    = setTimeout(() => controller.abort(), 8000);

        const res = await fetch(url, {
          signal:  controller.signal,
          headers: { "Cache-Control": "no-cache" },
        });
        clearTimeout(timeout);

        if (!res.ok) {
          if (res.status === 404) {
            // CSV not yet created for this scenario — degrade gracefully
            console.warn(`useBlastHandGenerator: CSV not found at ${url} — this scenario may not have data yet`);
            return { hand: randomHand(), rangeMap: new Map(), wasLive: false };
          }
          throw new Error(`HTTP ${res.status} loading ${url}`);
        }

        const text = await res.text();
        parsedHands = parseBlastRangeCSV(text);

        // Store in cache
        setCache(prev => ({ ...prev, [rangePath]: parsedHands }));
      }

      // Build the full (unfiltered) rangeMap up front — needed for scoring
      // regardless of which pool the dealt hand ends up coming from.
      // Key format: 'AA', 'AKs', 'AKo'. Hands with mixed strategies (e.g.
      // raise 50% + check 50%) produce multiple rows in the CSV; we accumulate
      // all rows in allActions so scoring can look up any specific action type.
      const rangeMap = new Map();
      parsedHands.forEach(({ hand, type, frequency, action }) => {
        const key = fullClassFromRow({ hand, type });
        const existing = rangeMap.get(key);
        if (existing) {
          existing.allActions.push({ action, frequency });
        } else {
          rangeMap.set(key, { action, frequency, allActions: [{ action, frequency }] });
        }
      });

      const usedHandKeys    = normalised.usedHandKeys;
      const forcedHandClass = normalised.forcedHandClass || null;
      const forceLive       = !!normalised.forceLive;

      // ── Forced return: a wrong-answer hand is due back this question. Deal
      // it directly — guaranteed, not just eligible to come up by chance. ──────
      if (forcedHandClass) {
        const forcedCards = specToCards({ hand: forcedHandClass });
        if (forcedCards) {
          const entry = rangeMap.get(forcedHandClass);
          const wasLive = !!entry && (entry.frequency > 0 || (entry.allActions || []).some(a => a.frequency > 0));
          console.log(`useBlastHandGenerator: forced return of ${forcedHandClass} (live=${wasLive}) from ${rangePath}`);
          return { hand: forcedCards, rangeMap, wasLive };
        }
      }

      // ── Apply filter & gate ───────────────────────────────────────────────
      let filtered = applyFilter(parsedHands, filterMode);

      // For reaction scenarios (open_raise / open_limp then facing villain response),
      // restrict to hands that were actually in the hero's opening range.
      const gateInfo = getGateRangeInfo(normalised);
      let gateKeys = null; // full reachable range for this node — only set when gated
      if (gateInfo) {
        let gateHands = cache[gateInfo.path];
        if (!gateHands) {
          try {
            const gateUrl = gateInfo.path.startsWith('/') ? gateInfo.path : `/${gateInfo.path}`;
            const gateRes = await fetch(gateUrl, { headers: { 'Cache-Control': 'no-cache' } });
            if (gateRes.ok) {
              const gateText = await gateRes.text();
              gateHands = parseBlastRangeCSV(gateText);
              setCache(prev => ({ ...prev, [gateInfo.path]: gateHands }));
            }
          } catch (e) {
            console.warn('useBlastHandGenerator: could not load gate range', gateInfo.path, e);
          }
        }
        if (gateHands?.length > 0) {
          gateKeys = buildGateKeySet(gateHands, gateInfo.filter);
          const gated = filtered.filter(({ hand, type }) => gateKeys.has(fullClassFromRow({ hand, type })));
          if (gated.length > 0) {
            filtered = gated;
          } else {
            console.warn('useBlastHandGenerator: gate produced 0 hands, falling back to unfiltered');
          }
        }
      }

      const eligible = filtered; // the curated "real decision" pool

      if (eligible.length === 0) {
        // This happens when the filter (e.g. 'raise') has no matching hands at
        // this stack — for example, SB at 15bb in a 3-way game only jams/limps,
        // never min-raises. Returning a random hand with an empty rangeMap would
        // cause every answer to be judged against correctAction='fold', producing
        // wrong scores and "You Selected Never" labels. Return null so the caller
        // can handle the error gracefully instead of silently showing a broken quiz.
        console.error(
          `useBlastHandGenerator: no eligible hands after filter="${filterMode}" at ${rangePath}. ` +
          `This stack may have no hands for this action type — check availableStacks config.`
        );
        return { hand: null, rangeMap: new Map(), wasLive: false };
      }

      const eligibleClassSet = new Set(eligible.map(fullClassFromRow));

      // ── Curated pool vs. general pool ─────────────────────────────────────
      // forceLive comes from GameContext's per-quiz quota (shouldForceBlastLiveHand)
      // — it guarantees a minimum fraction of real decisions per quiz on narrow
      // ranges, without making EVERY hand a real decision (which is what
      // sampling only from `eligible` used to do, and why fold hands never
      // appeared). When not forced, we draw from the full reachable hand
      // space, so genuine "obviously fold" hands can come up too.
      let cards = null;
      let wasLive = false;

      if (!forceLive) {
        if (gateKeys && gateKeys.size > 0) {
          // Reaction scenario: general pool = every hand reachable at this node
          // (hero's real limping/opening range), not just the ones with a live
          // decision here.
          const gateList = [...gateKeys];
          const usedFiltered = usedHandKeys && usedHandKeys.size > 0
            ? gateList.filter(c => !usedHandKeys.has(c.slice(0, 2)))
            : gateList;
          const dealtClass = pickWeightedClass(usedFiltered.length > 0 ? usedFiltered : gateList);
          if (dealtClass) {
            cards = specToCards({ hand: dealtClass });
            wasLive = eligibleClassSet.has(dealtClass);
          }
        } else {
          // Pure opening decision (or an ungated reaction, e.g. BB's own
          // first action): general pool = the full 52-card deck.
          const dealt = randomHandAvoiding(usedHandKeys);
          cards = dealt;
          const dealtClass = dealt.card1Value === dealt.card2Value
            ? dealt.card1Value + dealt.card2Value
            : dealt.card1Value + dealt.card2Value + (dealt.card1Suit === dealt.card2Suit ? 's' : 'o');
          wasLive = eligibleClassSet.has(dealtClass);
        }
      }

      if (forceLive || !cards) {
        // Curated pool: guaranteed a real, non-fold decision.
        const usedFiltered = usedHandKeys && usedHandKeys.size > 0
          ? eligible.filter(h => !usedHandKeys.has(h.hand))
          : eligible;
        const pool = usedFiltered.length > 0 ? usedFiltered : eligible;
        const selected = _.sample(pool);
        cards = specToCards({ hand: fullClassFromRow(selected) });
        wasLive = true;
      }

      if (!cards) {
        console.warn("useBlastHandGenerator: could not produce cards for config", normalised);
        return { hand: randomHand(), rangeMap, wasLive: false };
      }

      console.log(`useBlastHandGenerator: dealt hand (live=${wasLive}, forced=${forceLive}) from ${rangePath}`);
      return { hand: cards, rangeMap, wasLive };

    } catch (err) {
      console.error("useBlastHandGenerator error:", err);
      return { hand: randomHand(), rangeMap: new Map(), wasLive: false };
    }
  }, [cache]);

  return { generateBlastHand };
};

export default useBlastHandGenerator;
